import React, { useCallback } from 'react'
import './SingularProfileCard.scss'
import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import classNames from 'classnames'
import { PencilIcon } from '@assets/index'
import { getActiveProfile, isUserProfile, onEnterKeyPressAndClick } from '@utils/helpers'
import { useNavigate } from 'react-router-dom'
import { profileRoutes } from '@constants/routes.const'
import { UserProfile } from 'sdk/services/storage/profiles/types/profilesManager'
import { ProfileImagesData } from 'sdk/services/config/types/config'
import { useTypedSelector } from 'store'
import { ProfileIcon } from '../ProfileIcon/ProfileIcon'
import { LocalStorageItem, SessionStorageItem } from '@constants/index'
import { VelvetSDK } from 'sdk'
import { useDispatch } from 'react-redux'
import { fetchMenu } from '@actions/menuActions'

interface SingularProfileCardProps {
  profileData?: UserProfile | ProfileImagesData
  className?: string
  setActiveProfileOnEnter?: boolean
  profileName?: string
  isFocusable?: boolean
}

export const SingularProfileCard = ({
  profileData,
  className,
  setActiveProfileOnEnter = false,
  profileName,
  isFocusable = true,
}: SingularProfileCardProps) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const activeProfile = getActiveProfile()
  const initialRoute =
    useTypedSelector((state) => state.menu.initialRoute) || localStorage.getItem(LocalStorageItem.INITIAL_ROUTE)

  // Get the image data and other properties based on type
  const imageData = isUserProfile(profileData) ? profileData?.image : profileData
  const profileNameCard = isUserProfile(profileData) ? profileData?.name : undefined
  const profileIdentifier = isUserProfile(profileData) ? profileData?.identifier : profileData?.slug

  const handleNavigation = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      onEnterKeyPressAndClick(e, () => {
        e.stopPropagation()
        if (isUserProfile(profileData)) {
          navigate(`${profileRoutes.EDIT_PROFILE}/${profileData?.identifier}`)
        }
      })
    },
    [navigate, profileData],
  )

  const handleActivateProfile = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      setActiveProfileOnEnter &&
        onEnterKeyPressAndClick(e, async () => {
          e.stopPropagation()
          if (isUserProfile(profileData)) {
            localStorage.setItem(LocalStorageItem.ACTIVE_PROFILE, JSON.stringify(profileData))
            sessionStorage.removeItem(SessionStorageItem.LAST_FOCUSED_ITEM_HOME)

            if (profileData.preferredLanguage !== activeProfile?.preferredLanguage) {
              await VelvetSDK.Vocabulary.service.fetchVocabulary(profileData.preferredLanguage)
              await dispatch(fetchMenu(profileData.preferredLanguage))
            }

            initialRoute && navigate(initialRoute)
          }
        })
    },
    [initialRoute, navigate, profileData, setActiveProfileOnEnter, activeProfile?.preferredLanguage, dispatch],
  )

  return (
    <div
      className={classNames('singular-profile-wrapper', isFocusable && 'focusable', className)}
      id={profileIdentifier}
      tabIndex={-1}
      onClick={handleActivateProfile}
      onKeyDown={handleActivateProfile}
    >
      <div className="singular-profile">
        <div
          className={classNames('singular-profile-pic', imageData?.type)}
          style={{ backgroundColor: `${imageData?.backgroundColor}` }}
        >
          <ProfileIcon profileData={profileData} profileName={profileName} />
        </div>
        <Text tag={TextTag.P} className="profile-name">
          {profileNameCard || profileName}
        </Text>
      </div>
      {isUserProfile(profileData) && (
        <div
          className={classNames('edit-icon', 'focusable')}
          tabIndex={-1}
          onKeyDown={handleNavigation}
          onClick={handleNavigation}
        >
          <PencilIcon />
        </div>
      )}
    </div>
  )
}
